import { Flex, Text } from '@radix-ui/themes';
import React, { useEffect, useState, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; // Import the CSS for react-toastify
import debounce from 'lodash/debounce';
import { MdOutlineVerifiedUser } from 'react-icons/md';
import { VscUnverified, VscVerified } from 'react-icons/vsc';

import TableComponent from '../components/ui-components/TableComponent';
import CreateInstitution from '../components/ui-components/CreateInstitution';
import ViewInstitution from '../components/ui-components/ViewInstitution';
import SearchFilter from '../components/ui-components/SearchFilter';
import PaginationComponent from '../components/ui-components/PaginationComponent';
import axiosInstance from '../config/axiosConfig';
import { highlightSearchTerm } from '../utils/helpers';

const InstitutionsPage = () => {
  const [institutions, setInstitutions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [institutionTypes, setInstitutionTypes] = useState([]);
  const [licenceTypes, setLicenceTypes] = useState([]);
  const pageSize = 8;

  // Re-written showToast function to use react-toastify
  const showToast = (message, type = 'success') => {
    switch (type) {
      case 'success':
        toast.success(message);
        break;
      case 'error':
        toast.error(message);
        break;
      case 'info':
        toast.info(message);
        break;
      default:
        toast(message);
    }
  };

  // Fetch enums on page load
  useEffect(() => {
    const fetchEnums = async () => {
      try {
        const [instTypeRes, licTypeRes] = await Promise.all([
          axiosInstance.get('/api/enum/institution-types'),
          axiosInstance.get('/api/enum/licence-types'),
        ]);

        setInstitutionTypes(instTypeRes.data.result || []);
        setLicenceTypes(licTypeRes.data.result || []);
      } catch (error) {
        console.error('Error fetching enums:', error);
        showToast('Failed to load dropdown options', 'error'); // Use error type for toast
      }
    };
    fetchEnums();
  }, []); // Empty dependency array to run only once on mount

  const columns = [
    { title: "#", key: "serialNumber", width: "5%", render: (value) => <span>{value}</span> },
    {
      title: "Institution Name",
      key: "name",
      width: "15%",
      render: (value, row) => (
        <Flex gap="2" align="center">
          <Flex direction="column">
            <Text weight="bold" size="2">{highlightSearchTerm(value, searchQuery)}</Text>
            <span className="flex items-center gap-1">
              {highlightSearchTerm(row.email || "N/A", searchQuery)}
              {row.isEmailConfirmed ? <VscVerified color="green" /> : <VscUnverified color="tomato" />}
            </span>
          </Flex>
        </Flex>
      ),
    },
    {
      title: "Institution ID",
      key: "institutionNumber",
      width: "15%",
      render: (value) => <span>{highlightSearchTerm(value, searchQuery)}</span>,
    },
    {
      title: "Address",
      key: "address",
      width: "15%",
      render: (value) => <span>{highlightSearchTerm(value, searchQuery)}</span>,
    },
    {
      title: "Phone Number",
      key: "phoneNo",
      width: "15%",
      render: (value) => <span>{highlightSearchTerm(value, searchQuery)}</span>,
    },
    {
      title: "Status",
      key: "isActive",
      width: "10%",
      render: (value) => (
        <Flex align="center" gap="1">
          <MdOutlineVerifiedUser color={value ? "green" : "red"} />
          <span style={{ color: value ? "green" : "tomato" }}>{value ? "Active" : "Inactive"}</span>
        </Flex>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: "15%",
      render: (value, row) => (
        <Flex gap="2" align="center">
          <Flex direction="row" gap="1">
            <CreateInstitution
              initialValues={row}
              onInstitutionCreated={fetchInstitutions}
              showToast={showToast}
              institutionTypes={institutionTypes}
              licenceTypes={licenceTypes}
            />
            <ViewInstitution initialValues={row} onInstitutionUpdated={fetchInstitutions} showToast={showToast} />
          </Flex>
        </Flex>
      ),
    },
  ];

  const fetchInstitutions = useCallback(async (page = 1, query = searchQuery, activeOnly = showActiveOnly) => {
    try {
      const payload = {
        token: null,
        searchTerm: query.length >= 3 ? query : null,
        isActive: activeOnly ? true : null,
        pageNumber: page,
        pageSize: pageSize,
      };
      const response = await axiosInstance.post('/Institution/get', payload);
      if (response.data && response.data.result) {
        const institutionsData = response.data.result.institutionList.map((item, index) => ({
          ...item,
          serialNumber: (page - 1) * pageSize + index + 1,
        }));
        setInstitutions(institutionsData);
        setTotalCount(response.data.result.totalCount || 0);
      } else {
        setInstitutions([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching institutions:', error);
      showToast('Failed to fetch institutions', 'error'); // Use error type for toast
      setInstitutions([]);
      setTotalCount(0);
    }
  }, [searchQuery, showActiveOnly]);

  const debouncedFetch = useCallback(debounce((page, query, activeOnly) => {
    fetchInstitutions(page, query, activeOnly);
  }, 300), [fetchInstitutions]);

  useEffect(() => {
    debouncedFetch(currentPage, searchQuery, showActiveOnly);
    return () => debouncedFetch.cancel();
  }, [currentPage, searchQuery, showActiveOnly, debouncedFetch]);

  const handleSearchChange = () => {
    setCurrentPage(1);
  };

  const handleActiveToggle = () => {
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <>
      {/* ToastContainer is placed here to handle all toast notifications */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Flex direction="column" gap="2">
        <Flex justify="between" align="center" mb="4" gap="3" wrap="wrap">
          <SearchFilter
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            showActiveOnly={showActiveOnly}
            setShowActiveOnly={setShowActiveOnly}
            placeholder="Search institutions (min 3 chars)"
            onSearchChange={handleSearchChange}
            onActiveToggle={handleActiveToggle}
          />
          <CreateInstitution
            onInstitutionCreated={() => fetchInstitutions(1)}
            showToast={showToast}
            institutionTypes={institutionTypes}
            licenceTypes={licenceTypes}
          />
        </Flex>
        <TableComponent dataStructure={institutions} columns={columns} />
        {totalCount > 0 && (
          <PaginationComponent
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(newPage) => setCurrentPage(newPage)}
          />
        )}
        {totalCount > 0 && (
          <Text size="2" align="center" mt="2">
            Showing {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, totalCount)} of {totalCount} institutions
          </Text>
        )}
      </Flex>
    </>
  );
};

export default InstitutionsPage;